import { useEffect, useState, type CSSProperties } from "react";
import useSWR from "swr";
import { Box, Stack, Group, Text, Badge, Collapse, UnstyledButton, Select, Menu, Alert, Paper, Anchor } from "@mantine/core";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import ChevronsUpDown from "lucide-react/dist/esm/icons/chevrons-up-down";
import { useFetch, getReviewSubjects, setLectureReviewState, getSemesters, getVideoUrl, ReviewRole, ReviewState, REVIEW_STATES, ReviewSubject, ReviewLecture, PLATFORM_YOUTUBE, PLATFORM_VK } from "../../shared/api";
import { ErrorDisplay, Loading } from "../../shared/utils";
import classes from "./SubjectReviewList.module.css";
import youtubeIcon from "../../shared/assets/colored/youtube.png";
import vkIcon from "../../shared/assets/colored/vk.png";

const platformIcons = { [PLATFORM_YOUTUBE]: youtubeIcon, [PLATFORM_VK]: vkIcon };
export const REVIEW_STATE_LABEL: Record<ReviewState, string> = { pending: "Опубликовано", rework: "На доработке", approved: "Проверено" };
const REVIEW_STATE_COLOR: Record<ReviewState, string> = { pending: "blue", rework: "orange", approved: "green" };
const isOpen = (s: ReviewState) => s === "pending" || s === "rework";

function StatePill({ state, interactive = false }: { state: ReviewState; interactive?: boolean }) {
  const color = REVIEW_STATE_COLOR[state];
  return (
    <span className={classes.pill} style={{ "--pill-bg": `var(--mantine-color-${color}-light)`, "--pill-fg": `var(--mantine-color-${color}-light-color)`, "--pill-fg-dark": `var(--mantine-color-${color}-4)` } as CSSProperties}>
      {REVIEW_STATE_LABEL[state]}{interactive && <ChevronsUpDown size={13} />}
    </span>
  );
}

/** A subject's lectures, newest first: title, links to the published videos and the review state. */
export function LectureList({ lectures, editable, onChange, numbered = false }: { lectures: ReviewLecture[]; editable: boolean; onChange: (lectureId: number, state: ReviewState) => Promise<void>; numbered?: boolean }) {
  const sorted = [...lectures].sort((a, b) => b.number - a.number);
  return (
    <Stack gap={6}>
      {sorted.map(l => (
        <Group key={l.id} justify="space-between" wrap="nowrap" gap="md">
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            {numbered && <Text fz="sm" fw={500} style={{ flexShrink: 0, minWidth: 36 }}>№{l.number}</Text>}
            {(l.title || !numbered) && <Text fz="sm" truncate c={l.title ? undefined : "dimmed"}>{l.title ?? `Лекция ${l.number}`}</Text>}
            {l.publications.map(pub => (
              <Anchor key={pub.platform} href={getVideoUrl(pub.platform, pub.video_id, pub.channel_ref ?? '')} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, lineHeight: 0 }}>
                <img src={platformIcons[pub.platform]} alt={pub.platform} style={{ width: 16, height: 'auto', display: 'block' }} />
              </Anchor>
            ))}
          </Group>
          {editable ? (
            <Menu withinPortal position="bottom-end">
              <Menu.Target><UnstyledButton className={classes.pillButton} aria-label="Изменить статус"><StatePill state={l.review_state} interactive /></UnstyledButton></Menu.Target>
              <Menu.Dropdown>{REVIEW_STATES.map(s => <Menu.Item key={s} onClick={() => onChange(l.id, s)}>{REVIEW_STATE_LABEL[s]}</Menu.Item>)}</Menu.Dropdown>
            </Menu>
          ) : <div style={{ flexShrink: 0 }}><StatePill state={l.review_state} /></div>}
        </Group>
      ))}
      {sorted.length === 0 && <Text fz="sm" c="dimmed">Лекций пока нет</Text>}
    </Stack>
  );
}

/** Lectures still waiting on the reviewer: published or sent back for rework. */
export const openLectureCount = (lectures: ReviewLecture[]) => lectures.filter(l => isOpen(l.review_state)).length;

function SubjectRow({ subject, editable, onChange }: { subject: ReviewSubject; editable: boolean; onChange: (lectureId: number, state: ReviewState) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const openCount = openLectureCount(subject.lectures);

  return (
    <Paper withBorder radius="md" p="md">
      <UnstyledButton onClick={() => setOpen(o => !o)} w="100%">
        <Group gap={6} align="center" wrap="nowrap">
          <ChevronRight size={16} style={{ transition: "transform 150ms ease", transform: open ? "rotate(90deg)" : "none" }} />
          <Text fz="md" fw={700}>{subject.title}</Text>
          {openCount > 0 && <Badge circle size="sm" variant="filled">{openCount}</Badge>}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Box pl="lg" pt="sm"><LectureList lectures={subject.lectures} editable={editable} onChange={onChange} /></Box>
      </Collapse>
    </Paper>
  );
}

export function SubjectReviewList({ role, editable = false, emptyMessage }: { role: ReviewRole; editable?: boolean; emptyMessage: string }) {
  const { fetch } = useFetch();
  const [semesterId, setSemesterId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data: semesters, error: semestersError } = useSWR("review/semesters", () => getSemesters({ fetch }));

  useEffect(() => {
    if (ready) return;
    if (semesters) { const active = semesters.find(s => s.is_active); if (active) setSemesterId(active.id); setReady(true); }
    else if (semestersError) setReady(true);
  }, [semesters, semestersError, ready]);

  const { data, error, isLoading, mutate } = useSWR(ready ? ["review/subjects", role, semesterId] : null, () => getReviewSubjects({ role, semesterId, fetch }));

  async function change(lectureId: number, state: ReviewState) {
    setActionError(null);
    try { await setLectureReviewState({ lectureId, reviewState: state, fetch }); await mutate(); }
    catch (e: any) { setActionError(e?.message ?? String(e)); }
  }

  if (error) return <ErrorDisplay error={error} />;
  if (!ready || isLoading || !data) return <Loading />;

  return (
    <Stack>
      <Select w={260} label="Семестр" placeholder="Все семестры" data={(semesters ?? []).map(s => ({ value: s.id, label: s.title }))} value={semesterId} onChange={setSemesterId} clearable />
      {actionError && <Alert color="red">{actionError}</Alert>}
      {data.length === 0 ? <Text c="dimmed">{emptyMessage}</Text> : (
        <Stack gap="sm">{data.map(subject => <SubjectRow key={subject.id} subject={subject} editable={editable} onChange={change} />)}</Stack>
      )}
    </Stack>
  );
}
